import React, { useEffect, useState, useRef } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { Loader2 } from 'lucide-react';
import { getAvailableRoute } from '@/constants/constant-permission-route-mapping';
import { ERROR_UNAUTHORIZED } from '@/constants/constant-routes';
import useApplyUITheme, { useSiteTheme } from '@/hooks/useApplyUITheme';
import { UISite } from '@/services/ui-customization.service';
interface RouteGuardProps {
  children: React.ReactNode;
  requiredRole?: 'admin' | 'learner' | 'any';
  requireAuth?: boolean;
  redirectTo?: string;
}

export default function RouteGuard({
  children,
  requiredRole = 'any',
  requireAuth = true,
  redirectTo = ERROR_UNAUTHORIZED
}: RouteGuardProps) {
  const { isAuthenticated, isLoading, userMode } = useAuth();
  const location = useLocation();
  const [isChecking, setIsChecking] = useState(true);
  const previousMode = useRef(userMode.currentMode);

const site: UISite =
    requiredRole === 'admin' || location.pathname.startsWith('/admin')
      ? 'admin'
      : location.pathname.startsWith('/login') || location.pathname.startsWith('/auth')
        ? 'auth'
        : userMode.currentMode === 'admin'
          ? 'admin'
          : 'learner';

  useApplyUITheme(site);
  const { saved } = useSiteTheme(site);
  const primaryColor = saved?.primary_color;
  const pageBg = saved?.page_background;


  useEffect(() => {
    // Small delay to ensure auth state is properly loaded
    const timer = setTimeout(() => {
      setIsChecking(false);
    }, 100);

    return () => clearTimeout(timer);
  }, []);

  // Detect mode switch - when mode changes, show loading briefly
  useEffect(() => {
    if (previousMode.current !== userMode.currentMode) {
      previousMode.current = userMode.currentMode;
      setIsChecking(true);
      const timer = setTimeout(() => {
        setIsChecking(false);
      }, 150);
      return () => clearTimeout(timer);
    }
  }, [userMode.currentMode]);

  // Show loading while checking authentication or during mode switch
if (isLoading || isChecking) {
    return (
      <div
        className={`min-h-screen flex items-center justify-center transition-colors ${
          !pageBg ? 'bg-gray-50' : ''
        }`}
        style={pageBg ? { backgroundColor: pageBg } : undefined}
      >
        <div className="text-center">
          <Loader2
            className={`w-12 h-12 animate-spin mx-auto mb-4 ${
              !primaryColor ? 'text-primary-600' : ''
            }`}
            style={primaryColor ? { color: primaryColor } : undefined}
          />
          <p className="text-gray-600">Verifying access...</p>
        </div>
      </div>
    );
  }

  // Check authentication
  if (requireAuth && !isAuthenticated) {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  // If no specific role is required, allow access
  if (requiredRole === 'any') {
    return <>{children}</>;
  }

  // Check permission-based access: user must have at least one permission
  // mapped to a route under the required prefix (/admin or /learn)
  const userPermissions: string[] = JSON.parse(
    sessionStorage.getItem('permissions') || '[]'
  );
  const routePrefix = requiredRole === 'admin' ? '/admin' : '/learn';
  const hasRouteForPrefix = getAvailableRoute(userPermissions, routePrefix) !== null;

  if (!hasRouteForPrefix) {
    return <Navigate to={redirectTo} replace />;
  }

  return <>{children}</>;
}

// Export a HOC version for easier use
export function withRouteGuard<P extends object>(
  Component: React.ComponentType<P>,
  guardProps: Omit<RouteGuardProps, 'children'>
) {
  return (props: P) => (
    <RouteGuard {...guardProps}>
      <Component {...props} />
    </RouteGuard>
  );
}